/**
 * @file     typography.ts
 * @readme   ./README.md
 * @purpose  Defines the typography scale: the single Inter family (headings and body share it), the 56/48/36/28/22/18/16/14/12 size scale, the three-weight set (400/500/600), and the fluid hub display sizes.
 */
import type { TypographyScale } from '@/core/types';

export const typographyTokens: TypographyScale = {
  fontFamilyHeading: 'var(--font-sans), system-ui, sans-serif',
  fontFamilyBody: 'var(--font-sans), system-ui, sans-serif',
  display: 56,
  h1: 48,
  h2: 36,
  h3: 28,
  h4: 22,
  bodyLg: 18,
  bodyMd: 16,
  bodySm: 14,
  caption: 12
};

/**
 * Three weights carry the whole site. Hierarchy comes from size, colour and
 * space; 600 is the ceiling (display and section headings), 500 is UI chrome
 * (buttons, labels, card titles, figures), 400 is reading text.
 */
export const fontWeightTokens = {
  regular: 400,
  medium: 500,
  semibold: 600,
} as const;

/** Hub display type: sizes are the mobile floor and desktop ceiling in px. */
export const hubTypographyTokens = {
  h1: {
    mobile: 34,
    desktop: 60,
    fluid: 'clamp(34px, 4.2vw, 60px)',
    // Inter is variable: at display size 560 reads as semibold without the ink.
    fontWeight: 560,
    letterSpacing: '-0.022em',
    lineHeight: 1.06,
  },
  h2: {
    mobile: 26,
    desktop: 40,
    fluid: 'clamp(26px, 2.8vw, 40px)',
    fontWeight: fontWeightTokens.semibold,
    letterSpacing: '-0.018em',
    lineHeight: 1.12,
  },
  h3: {
    mobile: 20,
    desktop: 24,
    fluid: 'clamp(20px, 1.7vw, 24px)',
    fontWeight: fontWeightTokens.semibold,
    letterSpacing: '-0.012em',
    lineHeight: 1.25,
  },
  subhead: {
    mobile: 17,
    desktop: 20,
    fluid: 'clamp(17px, 1.4vw, 20px)',
    maxWidth: '46ch',
  },
} as const;
